import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Alert, Box, Typography } from "@mui/material";
import { contentApi, formData, rows } from "src/pages/website-content/api";
import { PostFunction } from "src/utils/axios_connection";
import UrlApi from "src/utils/Url";
import { dateText, imageUrl, isFile, logoOnSite, rights } from "src/pages/website-content/checks";
import ResourceList from "src/components/shared/ResourceList";
import { plural } from "src/components/shared/format";
import {
  ConfirmDialog,
  ContentPage,
  EditorDialog,
  Field,
  HonestyCheck,
  ImageField,
  ImagePreview,
  WhereLink,
  useConfirm,
  useContentList,
  useForm,
} from "src/pages/website-content/kit";
import { LogosPreview, SitePreview } from "src/pages/website-content/Preview";
import { looksLikeFileName, namesSupported, organisationName } from "./names";

// Logos of organisations that back Safrati, in "Trusted by" on the About
// page (logoOnSite mirrors the website's rule). Each logo sits in a white
// card 96 px tall, scaled to fit at most 56 px high.

const EMPTY = { id: null, name: "", image: "", agreed: false };

const FILE_NAME_WARNING = "This looks like a file name. Use the organisation’s name.";
const NAME_HINT = "The organisation, as visitors know it (for example “AUB Innovation Park”), not the logo’s file name.";

const post = (Route, data, dispatch, isMultipart = false, showerror = true) =>
  PostFunction({ Route, data, isMultipart, showalert: false, showerror, dispatch });

// The logo as the About page draws it.
function Logo({ src }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  return (
    <Box
      sx={{
        width: 200,
        height: 96,
        p: 2,
        borderRadius: "20px",
        border: 1,
        borderColor: "divider",
        bgcolor: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {src && !broken ? (
        <Box
          component="img"
          src={src}
          alt=""
          onError={() => setBroken(true)}
          sx={{ maxHeight: 56, maxWidth: "100%", width: "auto", objectFit: "contain" }}
        />
      ) : (
        <ImagePreview src="" width={56} height={56} border={false} background="#FFFFFF" />
      )}
    </Box>
  );
}

Logo.propTypes = { src: PropTypes.string };

const TrustedBy = () => {
  const dispatch = useDispatch();
  const can = rights("13");
  // The list says a failed load itself, with Try again, so no toast too.
  const load = useCallback(() => post(UrlApi.trusted_by.get, {}, dispatch, false, false).then(rows), [dispatch]);
  const list = useContentList(load);
  const [editing, setEditing] = useState(null);
  const form = useForm(EMPTY);
  const confirm = useConfirm();
  const items = list.items || [];
  // Until the API stores a name, the name shown is the logo's file name.
  const named = namesSupported(items);
  // The logo being added or replaced, for the preview of the About page's row.
  const previewSrc = useMemo(() => (isFile(form.values.image) ? URL.createObjectURL(form.values.image) : imageUrl(form.values.image)), [form.values.image]);
  useEffect(() => () => previewSrc?.startsWith?.("blob:") && URL.revokeObjectURL(previewSrc), [previewSrc]);

  const openEditor = (item) => {
    form.reset(item ? { id: item.id, name: item.name || "", image: item.image || "", agreed: true } : EMPTY);
    setEditing(item ? "edit" : "add");
  };

  const save = async () => {
    const v = form.values;
    const errors = {};
    if (named && !String(v.name || "").trim()) errors.name = "Write the organisation’s name.";
    // With names, a logo can be renamed without a new file.
    if (!isFile(v.image) && !(named && editing === "edit")) errors.image = editing === "edit" ? "Choose the new logo, or cancel." : "Choose the logo image.";
    if (!v.agreed) errors.agreed = "Tick this to confirm. If you can't, don't add the logo.";
    form.setErrors(errors);
    if (Object.keys(errors).length) return false;
    try {
      const name = String(v.name || "").trim();
      const image = isFile(v.image) ? v.image : undefined;
      if (editing === "edit") {
        if (named) await post(UrlApi.trusted_by.edit, formData({ trusted_by_id: v.id, name, image }), dispatch, true);
        else await contentApi.trustedBy.edit(v.id, v.image, dispatch);
        toast.success(image ? `The logo of ${name || "the organisation"} is replaced on the About page.` : `${name} saved.`);
      } else {
        if (named) await post(UrlApi.trusted_by.add, formData({ name, image }), dispatch, true);
        else await contentApi.trustedBy.add(v.image, dispatch);
        toast.success(`The logo of ${name || "the organisation"} is on the About page now, under “Supported by”.`);
      }
      setEditing(null);
      list.reload();
      return true;
    } catch {
      return undefined;
    }
  };

  return (
    <ContentPage
      title="Supported by"
      intro="Logos of organisations that support Safrati. The website shows them on the About page under “Supported by”, as named here."
      where={
        list.items ? (
          <>
            <strong>{plural(items.filter((l) => logoOnSite(l).shown).length, "logo")}</strong> show under “Supported by” on the{" "}
            <WhereLink page="about" />, in this order. A logo can’t be hidden yet, only deleted; only add organisations that agreed to be shown.
          </>
        ) : null
      }
      canAdd={can.write}
      addLabel="Add a logo"
      onAdd={() => openEditor(null)}
      dialogs={
        <>
          <EditorDialog
            open={!!editing}
            title={editing === "edit" ? (named ? "Rename or replace the logo" : "Replace logo") : "Add a logo"}
            subtitle="It shows on the About page, under “Supported by”, as soon as you save."
            onClose={() => setEditing(null)}
            onSave={save}
            saveLabel={editing === "edit" ? (named ? "Save logo" : "Replace logo") : "Add logo"}
            errors={form.errors}
            dirty={form.dirty}
          >
            {named ? (
              <Field
                label="Organisation name"
                value={form.values.name}
                onChange={(value) => form.set("name", value)}
                error={form.errors.name}
                maxLength={120}
                hint={looksLikeFileName(form.values.name) ? FILE_NAME_WARNING : NAME_HINT}
              />
            ) : (
              <Alert severity="info">
                The organisation’s name isn’t saved yet: this list names each logo after its file. Name the file after the organisation (for
                example “AUB Innovation Park.png”) before you upload it.
              </Alert>
            )}
            <ImageField
              label="Logo"
              value={form.values.image}
              onChange={(value) => form.set("image", value)}
              error={form.errors.image}
              canRemove={false}
              minWidth={300}
              hint="PNG or SVG with a transparent or white background, at least 300 px wide. The preview is the size visitors see."
              preview={(src) => <Logo src={src} />}
            />
            <SitePreview where="The About page, “Supported by”">
              <LogosPreview
                renderLogo={(src) => <Logo src={src} />}
                logos={(() => {
                  // The row as it will be: this logo (new or replaced) in its place.
                  const mine = previewSrc;
                  const shown = items.filter((l) => logoOnSite(l).shown);
                  if (editing === "edit") return shown.map((l) => (l.id === form.values.id ? { key: l.id, src: mine, isNew: true } : { key: l.id, src: imageUrl(l.image) }));
                  return [...shown.map((l) => ({ key: l.id, src: imageUrl(l.image) })), { key: "new", src: mine, isNew: true }];
                })()}
              />
            </SitePreview>
            <HonestyCheck checked={form.values.agreed} onChange={(value) => form.set("agreed", value)} error={form.errors.agreed}>
              This organisation works with Safrati and agreed to have its logo on the website.
            </HonestyCheck>
          </EditorDialog>

          <ConfirmDialog
            open={confirm.open}
            title={`Delete the logo of ${organisationName(confirm.item)}?`}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onClose={confirm.close}
            onConfirm={async () => {
              try {
                await contentApi.trustedBy.remove(confirm.item.id, dispatch);
                toast.success(`The logo of ${organisationName(confirm.item)} is off the About page.`);
                list.reload();
                return true;
              } catch {
                return false;
              }
            }}
          >
            <Box sx={{ my: 1 }}>
              <Logo src={imageUrl(confirm.item?.image)} />
            </Box>
            It comes off the About page. You can restore it from the Activity log.
          </ConfirmDialog>
        </>
      }
    >
      <ResourceList
        label="Logos"
        items={items}
        primaryLabel="Organisation"
        primary={(item) => {
          const site = logoOnSite(item);
          const name = organisationName(item);
          return {
            title: name,
            subtitle: (
              <>
                {[site.shown ? `Logo ${items.indexOf(item) + 1} on the About page` : site.reason, dateText(item.created_date) && `added ${dateText(item.created_date)}`]
                  .filter(Boolean)
                  .join(" · ")}
                {/* A quiet note, not an error: the data stays as it is. */}
                {looksLikeFileName(name) && (
                  <Typography component="span" variant="body2" sx={{ display: "block", color: "warning.dark" }}>
                    {named
                      ? `${FILE_NAME_WARNING}${can.edit ? " Open it to rename." : ""}`
                      : "This is the logo’s file name, not the organisation’s."}
                  </Typography>
                )}
              </>
            ),
          };
        }}
        columns={[{ key: "preview", label: "As on the website", width: 240, render: (item) => <Logo src={imageUrl(item.image)} /> }]}
        onOpen={can.edit ? openEditor : undefined}
        openLabel={(item) => `${named ? "Edit" : "Replace"} ${organisationName(item)}`}
        rowActions={(item) => [
          can.edit && { label: named ? "Rename or replace the logo" : "Replace the logo", onClick: () => openEditor(item) },
          can.delete && { label: "Delete", destructive: true, onClick: () => confirm.ask(item) },
        ]}
        loading={list.loading}
        error={list.failed}
        onRetry={list.reload}
        empty={{ title: "No logos yet.", hint: "The About page leaves out “Supported by” until one is added." }}
      />
    </ContentPage>
  );
};

export default TrustedBy;
